/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * Tenant module entitlements, fetched once and shared.
 *
 * `useModuleEntitlements()` keeps its shape for the sixteen places that call
 * it, but the fetch now happens in ModuleEntitlementsProvider (mounted in
 * App.js) instead of once per caller. Outside a provider, in tests or a
 * detached tree, the hook still fetches on its own.
 *
 * Alongside the tenant's entitled codes the response carries `catalog`: every
 * module the platform sells with the route keys and sidenav entries it gates.
 * The router filter and the sidenav derive their gating from it, falling back
 * to the checked-in snapshot until the first response arrives.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { setReVariant } from "layouts/RealEstate/variant";
import { useAuth } from "../auth/Auth";
import registrySnapshot from "../generated/moduleRegistry.json";
import { deriveEditionsFromModules } from "../moduleEntitlementMap";

export { deriveEditionsFromModules };

const SNAPSHOT_EDITIONS = Array.isArray(registrySnapshot.editions)
  ? registrySnapshot.editions
  : [];
const SNAPSHOT_FIELD_TAGS = registrySnapshot.field_tags || {};

const MAX_TRANSIENT_RETRIES = 5;
const RETRY_DELAY_MS = 1500;

const ModuleEntitlementsContext = createContext(null);

function useModuleEntitlementsState({ enabled = true } = {}) {
  const {
    axiosInstance,
    token,
    isAuthReady,
    isAuthenticated,
  } = useAuth();
  const [moduleCodes, setModuleCodes] = useState([]);
  const [modules, setModules] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [subscription, setSubscription] = useState(null);
  // null until a response carries them; the snapshot / module-derived values
  // apply meanwhile.
  const [liveEditions, setLiveEditions] = useState(null);
  const [liveEditionCatalog, setLiveEditionCatalog] = useState(null);
  const [liveFieldTags, setLiveFieldTags] = useState(null);
  const [appData, setAppData] = useState({ apps: [], defaultApp: null, ownedRouteKeys: [] });
  const [realEstate, setRealEstate] = useState({ variant: null, hiddenModules: [] });
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const moduleCodesRef = useRef(moduleCodes);
  const modulesRef = useRef(modules);
  const hasLoadedOnceRef = useRef(false);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef(null);

  useEffect(() => {
    moduleCodesRef.current = moduleCodes;
  }, [moduleCodes]);

  useEffect(() => {
    modulesRef.current = modules;
  }, [modules]);

  useEffect(() => {
    return () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
      }
    };
  }, []);

  const fetchEntitlements = useCallback(async () => {
    // Token handoff updates token state before /verify finishes. Do not issue
    // protected requests until bootstrap has authenticated the new origin.
    if (!enabled || !isAuthReady || !isAuthenticated || !axiosInstance || !token) {
      return;
    }

    try {
      const response = await axiosInstance.get("/module-entitlements");
      const codes = Array.isArray(response.data?.module_codes)
        ? response.data.module_codes
        : [];
      const moduleList = Array.isArray(response.data?.modules)
        ? response.data.modules
        : [];
      setModuleCodes(codes);
      setModules(moduleList);
      if (Array.isArray(response.data?.catalog) && response.data.catalog.length > 0) {
        setCatalog(response.data.catalog);
      }
      setSubscription(response.data?.subscription ?? null);
      if (Array.isArray(response.data?.editions)) {
        setLiveEditions(response.data.editions);
      }
      if (Array.isArray(response.data?.edition_catalog) && response.data.edition_catalog.length > 0) {
        setLiveEditionCatalog(response.data.edition_catalog);
      }
      if (response.data?.field_tags && typeof response.data.field_tags === "object") {
        setLiveFieldTags(response.data.field_tags);
      }
      // Real Estate country variant and the RE modules the tenant's personas
      // hide from navigation (backend/app/real_estate/settings_service.py).
      // The RE screens' formatters read the variant module-wide (variant.js).
      setReVariant(response.data?.re_variant || null);
      setRealEstate({
        variant: response.data?.re_variant || null,
        hiddenModules: Array.isArray(response.data?.re_hidden_modules)
          ? response.data.re_hidden_modules.map((code) => String(code).toUpperCase())
          : [],
      });
      // UI v2 apps (navigation presets); absent on a backend that predates them.
      setAppData({
        apps: Array.isArray(response.data?.apps) ? response.data.apps : [],
        defaultApp: response.data?.default_app || null,
        ownedRouteKeys: Array.isArray(response.data?.app_owned_route_keys) ? response.data.app_owned_route_keys : [],
      });
      hasLoadedOnceRef.current = true;
      setHasLoadedOnce(true);
      retryCountRef.current = 0;
    } catch (error) {
      console.error("Error fetching module entitlements:", error);
      // Keep prior codes on transient failures to avoid permanent deny-all.
      // Editions and field tags are simply left as they were.
      setModuleCodes(moduleCodesRef.current);
      setModules(modulesRef.current);

      if (
        !hasLoadedOnceRef.current &&
        retryCountRef.current < MAX_TRANSIENT_RETRIES
      ) {
        retryCountRef.current += 1;
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        retryTimerRef.current = setTimeout(() => {
          fetchEntitlements();
        }, RETRY_DELAY_MS);
      }
    }
  }, [enabled, axiosInstance, isAuthReady, isAuthenticated, token]);

  useEffect(() => {
    retryCountRef.current = 0;
    fetchEntitlements();
  }, [fetchEntitlements]);

  const enabledModuleCodes = useMemo(
    () => new Set(moduleCodes.map((code) => String(code).toUpperCase())),
    [moduleCodes]
  );

  const isModuleEnabled = useCallback(
    (code) => {
      if (!code) return false;
      return enabledModuleCodes.has(String(code).toUpperCase());
    },
    [enabledModuleCodes]
  );

  const hasTravelEntitlement = isModuleEnabled("TRAVEL");

  const editionCatalog = liveEditionCatalog || SNAPSHOT_EDITIONS;
  const fieldTags = liveFieldTags || SNAPSHOT_FIELD_TAGS;

  const enabledEditions = useMemo(() => {
    if (Array.isArray(liveEditions)) {
      return new Set(liveEditions.map((code) => String(code).toUpperCase()));
    }
    return deriveEditionsFromModules(enabledModuleCodes, catalog, liveEditionCatalog);
  }, [liveEditions, enabledModuleCodes, catalog, liveEditionCatalog]);

  const editions = useMemo(() => [...enabledEditions], [enabledEditions]);

  const hasEdition = useCallback(
    (code) => {
      if (!code) return false;
      return enabledEditions.has(String(code).toUpperCase());
    },
    [enabledEditions]
  );

  return useMemo(
    () => ({
      modules,
      moduleCodes,
      enabledModuleCodes,
      isModuleEnabled,
      hasTravelEntitlement,
      // Industry editions the tenant holds (["TRAVEL", "REAL_ESTATE"]), the
      // edition catalog, and which object columns belong to which edition.
      editions,
      enabledEditions,
      hasEdition,
      editionCatalog,
      fieldTags,
      // Every sellable module with its route keys and sidenav entries; null
      // until the first response, when the snapshot applies.
      catalog,
      subscription,
      // UI v2 apps available to this tenant, the default one, and every route
      // key some app owns (keys outside it are shared by all apps).
      apps: appData.apps,
      defaultApp: appData.defaultApp,
      appOwnedRouteKeys: appData.ownedRouteKeys,
      // Real Estate: the tenant's variant pack (UAE / AU) and the RE modules
      // its personas take out of navigation. Navigation only, not access.
      reVariant: realEstate.variant,
      reHiddenModules: realEstate.hiddenModules,
      // Not ready until first successful fetch — prevents empty-Set deny-all.
      loading: !hasLoadedOnce,
      refetch: fetchEntitlements,
    }),
    [
      modules,
      moduleCodes,
      enabledModuleCodes,
      isModuleEnabled,
      hasTravelEntitlement,
      editions,
      enabledEditions,
      hasEdition,
      editionCatalog,
      fieldTags,
      catalog,
      subscription,
      appData,
      realEstate,
      hasLoadedOnce,
      fetchEntitlements,
    ]
  );
}

export function ModuleEntitlementsProvider({ children }) {
  const value = useModuleEntitlementsState();
  return (
    <ModuleEntitlementsContext.Provider value={value}>
      {children}
    </ModuleEntitlementsContext.Provider>
  );
}

export function useModuleEntitlements() {
  const shared = useContext(ModuleEntitlementsContext);
  // Hooks run unconditionally; the standalone copy simply never fetches when
  // a provider is present.
  const standalone = useModuleEntitlementsState({ enabled: !shared });
  return shared || standalone;
}
